import { API_PATHS, REQUEST_METHODS, ROUTE_PARAMS, interpolatePathParams } from '@futrli/shared-services';
import { ConsolidatedForecastDetail, ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';

import { BulkUpdatedPredictionsPayload } from '../../ui/assistant-side-panel/bulk-update/types';
import {
  ConsolidatedForecastCreateData,
  ConsolidatedForecastUpdateData,
  ConsolidationSubEntityForecastsResponseData
} from './types/consolidated-forecasting';
import { ConsolidatedForecastReport, QueryParams } from '../types';
import { makeRequestWithResponse } from './shared';

/**
 * Creates a single consolidated forecast for a consolidation
 *
 * @param workspaceId - workspace to get the organisations from
 * @param consolidationId - organisation to get forecasts from
 * @param data - The forecast creation data to send with the request
 */
export const createConsolidatedForecast = async (
  workspaceId: string,
  consolidationId: string,
  data: ConsolidatedForecastCreateData
) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATED_FORECASTS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<ConsolidatedForecastListItem>({
    data: { data },
    url: path,
    method: REQUEST_METHODS.POST
  });
};

/**
 * Fetches a list of all forecasts for a consolidation
 *
 * @param workspaceId - workspace to get the organisations from
 * @param consolidationId - organisation to get forecasts from
 */
export const readConsolidatedForecasts = async (workspaceId: string, consolidationId: string) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATED_FORECASTS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<ConsolidatedForecastListItem[]>({
    url: path,
    method: REQUEST_METHODS.GET
  });
};

/**
 * Fetches a list of all forecasts for all of the entities within a consolidation
 *
 * @param workspaceId - workspace to get the organisations from
 * @param consolidationId - organisation to get forecasts from
 */
export const readSubEntityForecasts = async (workspaceId: string, consolidationId: string) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_SUB_ENTITIY_FORECASTS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<ConsolidationSubEntityForecastsResponseData>({
    url: path,
    method: REQUEST_METHODS.GET
  });
};

/**
 * Reads a single consolidated forecast for a consolidation
 *
 * @param workspaceId - workspace to get the organisations from
 * @param consolidationId - organisation to get forecasts from
 * @param forecastId - the id of the forecast to load
 */
export const readConsolidatedForecast = async (workspaceId: string, consolidationId: string, forecastId: string) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATED_FORECAST_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<ConsolidatedForecastDetail>({
    url: path,
    method: REQUEST_METHODS.GET
  });
};

/**
 * Reads a consolidation forecast report
 *
 * @param workspaceId - workspace to get the organisations from
 * @param consolidationId - consolidation to get forecasts from
 * @param forecastId - the id of the forecast to load
 */
export const readConsolidatedForecastReport = async (
  workspaceId: string,
  consolidationId: string,
  forecastId: string,
  queryParams: QueryParams = {}
) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATED_FORECAST_REPORT, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<ConsolidatedForecastReport>({
    url: path,
    method: REQUEST_METHODS.GET,
    params: queryParams
  });
};

/**
 * Updates a single consolidated forecast for a consolidation
 *
 * @param workspaceId - workspace to get the organisations from
 * @param consolidationId - organisation to get forecasts from
 * @param forecastId - the id of the forecast to update
 * @param data - the data to update the forecast with
 */
export const updateConsolidatedForecast = async (
  workspaceId: string,
  consolidationId: string,
  forecastId: string,
  data: ConsolidatedForecastUpdateData
) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATED_FORECAST_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<ConsolidatedForecastListItem>({
    data,
    url: path,
    method: REQUEST_METHODS.PATCH
  });
};

/**
 * Deletes a single consolidated forecast for a consolidation
 *
 * @param workspaceId - workspace to get the organisations from
 * @param consolidationId - organisation to get forecasts from
 * @param forecastId - the id of the forecast to delete
 */
export const deleteConsolidatedForecast = async (workspaceId: string, consolidationId: string, forecastId: string) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATED_FORECAST_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<void>({ url: path, method: REQUEST_METHODS.DELETE });
};

export const bulkUpdateConsolidatedForecast = async (
  data: Partial<BulkUpdatedPredictionsPayload>,
  workspaceId: string,
  consolidationId: string,
  forecastId: string
): Promise<void> => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATED_FORECAST_BY_ID_BULK_UPDATE, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<void>({ data, url: path, method: REQUEST_METHODS.PATCH });
};
